import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'quiet' | 'danger';

const base =
  'inline-flex items-center justify-center gap-2 rounded-(--radius-control) ' +
  'font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-45 ' +
  'min-h-11 px-4 text-base';

// Ink, not colour. The signal ochre is reserved for marking "now" in the data,
// so a primary action must not compete with it.
const variants: Record<Variant, string> = {
  primary: 'bg-ink text-paper hover:bg-ink-muted',
  secondary: 'border border-line-strong bg-surface text-ink hover:border-ink',
  quiet: 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
  danger: 'border border-risk/40 bg-surface text-risk hover:bg-risk hover:text-surface',
};

export function Button({
  variant = 'secondary',
  busy = false,
  children,
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  busy?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      // Buttons default to submit inside a form, which turns any action button
      // dropped into one into a save. Every form in this app already declares
      // its submit explicitly, so the safe default costs nothing and removes a
      // bug that is invisible until something is silently created.
      type="button"
      {...rest}
      disabled={rest.disabled || busy}
      aria-busy={busy || undefined}
      className={`${base} ${variants[variant]} ${className}`}
    >
      {busy ? <Spinner /> : null}
      {children}
    </button>
  );
}

export function Spinner({ className = '' }: { className?: string }) {
  return (
    <span
      role="status"
      aria-live="polite"
      className={`inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    />
  );
}
